/** Mock shop/pricing data, ported from the handoff `pricing.jsx`. */

/** The fixed, type-derived category the products endpoint assigns. */
export type ProductCategory =
  "specials" | "memberships" | "packs" | "giftCards";

export interface MockProduct {
  deferredChargeSettings?:
    import("@zipper/embedded-api-client").DeferredChargeSettingsDto | undefined;
  id: string;
  name: string;
  price: number;
  cadence: string;
  description: string;
  callout?: string;
  /**
   * The product's type-derived category. Carried on the product itself (not
   * just implied by which `ShopProducts` bucket it landed in) because the shop
   * ALSO renders products grouped into the provider's Manage-Shop-View
   * subcategories, where the buckets are not used at all — a gift card sitting
   * in a custom subcategory must still be recognisable as a gift card.
   */
  category?: ProductCategory;
  /** Short feature bullets shown as a checklist in the purchase order summary. */
  features?: string[];
  /** Real Stripe product id — populated from live data; empty in mock. */
  stripeProductId?: string;
  /** Intro/new-client-only — hidden for signed-in members in shop. */
  firstTimeClientsOnly?: boolean;
  /**
   * F3: id of the form (waiver/terms) assigned to this pricing option, if
   * any — populated from live data only (`Package.packageForms`). Absent in
   * mock data, which is the correct "no form assigned" default.
   */
  formId?: number;
}

export const MOCK_SPECIALS: MockProduct[] = [
  {
    id: 'discovery',
    name: 'Discovery Session',
    price: 95,
    cadence: 'one-time',
    description:
      'A one-on-one private session designed to help you explore whether Pilates fits your lifestyle. Perfect for beginners unsure about Pilates.',
    features: ['One-on-one private session', 'For first-timers', 'Book any time'],
  },
  {
    id: 'intro-classes',
    name: 'Intro 3-Pack of Classes',
    price: 99,
    cadence: 'one-time',
    description:
      'Three group classes to use at your convenience. A great way to see if Pilates is right for you. Expires 30 days from purchase.',
    features: ['3 group classes', '30 days to use', 'New students only'],
  },
  {
    id: 'intro-privates',
    name: 'Intro 3-Pack of Privates',
    price: 300,
    cadence: 'one-time',
    description:
      'Three one-on-one private sessions tailored to your goals. Ideal for those preparing for group classes. Expires 30 days from purchase.',
    features: ['3 private sessions', '30 days to use', 'New students only'],
  },
];

export const MOCK_MEMBERSHIPS: MockProduct[] = [
  {
    id: 'mem-4',
    name: '4 Classes / month',
    price: 129,
    cadence: 'per month',
    description:
      'Access to four Pilates classes a month. Includes all three studios and exclusive members-only sessions.',
    features: ['4 classes a month', 'All three studios', 'Members-only sessions'],
  },
  {
    id: 'mem-8',
    name: '8 Classes / month',
    price: 229,
    cadence: 'per month',
    callout: 'Most popular',
    description:
      'Eight classes a month for the regular practitioner. Includes all studios, members-only sessions, and priority booking.',
    features: ['8 classes a month', 'Priority booking', 'Members-only sessions'],
  },
  {
    id: 'mem-unlimited',
    name: 'Unlimited Monthly',
    price: 289,
    cadence: 'per month',
    callout: 'Best value',
    description:
      'Take as many classes as you like, up to one per day. Members-only sessions, workshops, and guest passes included.',
    features: ['Unlimited classes', 'Workshops included', 'Guest passes included'],
  },
];

export const MOCK_PACKS: MockProduct[] = [
  {
    id: 'pack-5-mat',
    name: '5-Pack Mat Classes',
    price: 155,
    cadence: 'one-time',
    description:
      'Five mat class punches, clearly labeled on the schedule. Cards expire six weeks from the date of purchase.',
  },
  {
    id: 'pack-5-equip',
    name: '5-Pack Equipment',
    price: 215,
    cadence: 'one-time',
    description:
      'Five classes across Reformer, Chair, Springboard, Jumpboard and more. Must be used within six weeks of purchase.',
  },
  {
    id: 'pack-10-mat',
    name: '10-Pack Mat Classes',
    price: 295,
    cadence: 'one-time',
    description:
      'Ten mat class punches with extended use. Cards expire three months from the date of purchase.',
  },
  {
    id: 'pack-10-equip',
    name: '10-Pack Equipment',
    price: 395,
    cadence: 'one-time',
    description:
      'Ten classes across all equipment offerings — Reformer, Chair, Springboard, Jumpboard, Open Studio and more. Expires three months from purchase.',
  },
];

export const MOCK_GIFT_CARDS: MockProduct[] = [
  {
    id: 'gift-50',
    name: 'Gift Card · $50',
    price: 50,
    cadence: 'one-time',
    category: 'giftCards',
    description: 'Redeemable for any class, private session, or pack.',
  },
  {
    id: 'gift-100',
    name: 'Gift Card · $100',
    price: 100,
    cadence: 'one-time',
    category: 'giftCards',
    description: 'Redeemable for any class, private session, or pack.',
  },
];

// ── Gift cards ──────────────────────────────────────────────────────────────
//
// A gift card is not an ordinary product. `POST /gift-cards/purchase-intent`
// (GiftCardService.createPurchaseIntent) rejects anything outside $5–$1,000, so
// these mirror the server's own bounds. Keep them in sync with
// `MIN_GIFT_CARD_AMOUNT` / the 100000 ceiling in `gift-card.service.ts`.
export const GIFT_MIN_CENTS = 500;
export const GIFT_MAX_CENTS = 100_000;

/**
 * Whether this catalog entry is a gift card rather than a normal purchasable.
 *
 * Gift-card-ness comes from the PRODUCT, never from a buyer choice: the
 * products endpoint maps `Package.type === 'custom-amount'` to
 * `category: 'giftCards'` and labels its cadence `custom`. A membership or pack
 * can never be bought "as a gift card".
 */
export function isGiftCardProduct(
  p: Pick<MockProduct, 'category' | 'cadence'>,
): boolean {
  return p.category === 'giftCards' || p.cadence === 'custom';
}

/**
 * Whether a gift card carries a real, fixed denomination that should simply be
 * charged, versus a placeholder price that means "ask the buyer".
 *
 * The embed cannot see `Package.type` — every gift card reaching it is already
 * `custom-amount` — so the price itself is the only available signal. The rule:
 * a catalog price at or above the server's own $5 minimum is a genuine
 * denomination (a "Gift Card" listed at $50); anything below it cannot be a
 * sellable gift-card value at all, because `/gift-cards/purchase-intent` would
 * reject it, so it can only be a placeholder. That is exactly what the real
 * data looks like — "Enter your price!" and "Customer choose" at $1, "Big
 * Donation" at $0 all fall below the floor and must prompt for an amount.
 */
export function isFixedDenominationGift(
  p: Pick<MockProduct, 'category' | 'cadence' | 'price'>,
): boolean {
  if (!isGiftCardProduct(p)) return false;
  return isFixedGiftAmount(p.price);
}

/**
 * The price half of {@link isFixedDenominationGift}, for callers that already
 * know they are looking at a gift card and only hold its price (the checkout
 * receives flat properties, not the catalog entry). Single source of truth for
 * the threshold so the shop and the checkout can never disagree about whether
 * a given card asks the buyer for an amount.
 */
export function isFixedGiftAmount(priceInDollars: number): boolean {
  return Math.round((priceInDollars || 0) * 100) >= GIFT_MIN_CENTS;
}
